/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { makeStyles, createStyles } from '@material-ui/core';
import cn from 'classnames';
import useDictionary from '../../dictionaries/context/useDictionary';
import { BankType, NegotiationOption, NegotiationState, OutDebt } from '../../generated/graphql';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { useGetNegotiationExpiredActionInfo } from '../../utils/useGetNegotiationExpiredActionInfo';
import AlertBase from './AlertBase';
import { useNegotiationStateAlertContext, UseNegotiationStateAlertContextProps } from './useNegotiationStateAlertContext';
import { TranquiTheme } from '../../containers/App/theme';
import MarkdownRender from '../../dictionaries/MarkdownRender';
import { getCustomContentText } from '../../utils/getCustomContentText';
import { getContentExtraBankNegType } from '../../utils/getContentExtraBankNegType';
import { getCommissionPercentText } from '../../utils/getCommissionPercentText';
import { getWeightedRateText } from '../../utils/getWeightedRateText';
import { getDaysBehindLimitKeyRappi } from '../../utils/getDaysBehindLimitKeyRappi';
import { useSpace } from '../../content/utils/useSpace';
import { shouldHideBogotaSpecialCampaignPaymentAlert } from '../../utils/shouldHideBogotaSpecialCampaignPaymentAlert';

export type NegotiationStateAlertProps = UseNegotiationStateAlertContextProps & {
  className?: string;
  // eslint no reconoce el uso en useGetTextKey
  // eslint-disable-next-line react/no-unused-prop-types
  negOptions?: NegotiationOption[];
  inSummary?: boolean;
  outDebts?: OutDebt[]
};

const NegotiationStateAlert: React.FC<NegotiationStateAlertProps> = (props) => {
  const {
    className, outDebt, extraInfo: propsExtraInfo,
    option, alternative, inSummary, outDebts, children,
  } = props;
  const extraInfo = outDebt.negotiation?.extra.info || propsExtraInfo;
  const { gT } = useDictionary();
  const title = gT(getTitleKey(props));
  const textKey = useGetTextKey(props);
  const contentText = getCustomContentText({
    gT,
    outDebt,
    negOption: option,
    outDebts,
    alternative,
  });
  const commissionText = getCommissionPercentText(gT, 'commissionAuxiliarText', alternative?.commissionPercent || 0);
  const commissionPercentBulletText = getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0);
  const context = useNegotiationStateAlertContext({
    ...props,
    extraInfo,
    commissionText,
    commissionPercentBulletText,
    weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
  });
  const negBooleans = getNegBooleans(outDebt, option, alternative);
  const {
    isExpired, isRejected, isConfirmedByUser, isTotalPayment, isPaymentPromise,
    isBbva, isRappiPay, isRappiPrestamo, isBogota, isBalanceBehindPayment, isInstallmentsPlan,
    isJcap, isApprovedByBank, isSentToBank, isRefinance, isBogotaTraditional,
    isBogotaPrepaymentPreferentialRate, isBogotaPrepaymentWeightedRate, isApex, negState, isPra,
    isBogotaTermExtension, isBogotaSpecialCampaign,
  } = negBooleans;
  const classes = useStyles();
  const isAlertBank = isRappiPay || isRappiPrestamo;

  // space implementation

  const { bank, brand, negPaymentDate, productNumber, ...cleanContext } = context;
  const stateAlertSpace = useSpace('NEG_OPTION', 'state.alert', {
    outDebt, option, alternative, ...cleanContext, negPaymentDate, productNumber,
  });
  // TODO: todo esto se puede simplificar, pero no lo hago todavía
  // porque no hemos completado las pruebas hasta esta page
  // para poder comprobar que no haya efectos inesperados
  if (stateAlertSpace.exists && !stateAlertSpace.visible) return null;
  const spaceContent = stateAlertSpace.exists && stateAlertSpace.text;

  // The summary already shows these terms; show this alert after negotiation creation.
  if (shouldHideBogotaSpecialCampaignPaymentAlert(
    inSummary,
    isBogotaSpecialCampaign,
    !!outDebt.negotiation,
  )) {
    return <>{children}</>;
  }

  if (
    isBogota
    && (((isTotalPayment || isRefinance || isBogotaTermExtension) && isConfirmedByUser)
      || (isInstallmentsPlan && isSentToBank))
  ) return null;

  if (isApex && ((negState !== NegotiationState.ConfirmedByUser
      && negState !== NegotiationState.SentToBank))) {
    return null;
  }

  if (isPra && (
    negState === NegotiationState.PaymentInProgress
    || negState === NegotiationState.ApprovedByBank)) return null;
  if (inSummary && isApex) return null;

  if (isPaymentPromise && !isAlertBank && !isBbva) return null;

  const isAllowedBankNegOptions = isBbva
    || isJcap
    || (isBogota && (isBalanceBehindPayment || isTotalPayment || isInstallmentsPlan));

  // si no tiene ni fecha ni día de pago y es confirmado por el usuario, no se muestra
  if (!isAllowedBankNegOptions
    && ((!context.newPaymentDay && !context.negPaymentDate) && isConfirmedByUser)) return null;
  // si es confirmado por el usuario y no es pago total y no tiene abono, no se muestra
  if (!isAllowedBankNegOptions
    && inSummary && !isTotalPayment && !alternative.prePayment) return null;

  // Esta alerta no aparece en pago total JCAP en estado aprobado por el banco
  if (isJcap && isTotalPayment && isApprovedByBank) return null;

  if (
    isBogota && isRefinance
    && (isBogotaTraditional || isBogotaPrepaymentPreferentialRate || isBogotaPrepaymentWeightedRate)
    && (isSentToBank || isApprovedByBank)
  ) return null;

  let content = contentText || gT(textKey, context);

  if (isBogota && isBalanceBehindPayment) {
    content = [ gT(textKey, context), contentText ].join(' ');
  }

  // this'll help us move gradually from legacy keys to spaces
  if (!spaceContent && !content) return null;

  return (
    <AlertBase
      type={isExpired || isRejected ? 'restrictions' : 'alert'}
      className={cn(className, classes.alert)}
      dataTest="negotiation-state-alert__payment-alert"
      title={title}
    >
      <MarkdownRender text={spaceContent || content} />
      {children}
    </AlertBase>
  );
};

const getTitleKey = (props: NegotiationStateAlertProps) => {
  const { isApprovedByBank } = getNegBooleans(props.outDebt);
  if (isApprovedByBank) return 'alert_paymentAlert_titleRemind';
  return 'alert_paymentAlert_title';
};

const useGetTextKey = (props: NegotiationStateAlertProps) => {
  const { outDebt, alternative, option, negOptions = [] } = props;
  const {
    negState, isConfirmedByUser, isVirtualDebt, isTotalPayment, isExpired, isPaymentPromise,
    isRappiPay, isRappiPrestamo, isRestructure, isRefinance, isDav, isNormalization, isACPK,
    isBogota, isRejected, isBalanceBehindPayment, isScotiabank, isJcap,
    isQuotification, isCatchUp,
    isMovistarMoney,
  } = getNegBooleans(outDebt, option, alternative);

  const isAdn = outDebt.bank === BankType.Bbva && outDebt.subType === 'ADN';

  const {
    sameAvailable, otherAvailable, noneAvailable,
  } = useGetNegotiationExpiredActionInfo({ outDebt, negOptions });

  let stateExtra = '';
  if (isExpired) {
    if (sameAvailable) stateExtra = '_sameAvailable';
    if (otherAvailable) stateExtra = '_otherAvailable';
    if (noneAvailable) stateExtra = '_noneAvailable';
  }
  const { SentToBank, ApprovedByBank, RejectedByBank, Expired } = NegotiationState;
  const state = `${negState}${stateExtra}`;

  if (isAdn && [ SentToBank, ApprovedByBank, RejectedByBank, Expired ].includes(negState)) stateExtra = '_ADN';

  if (isBogota && !!outDebt.daysBehind && isRefinance) {
    const baseKey = 'alert_paymentAlert_content';
    const prePayment = alternative.prePayment ? '_prePayment' : '';
    const blocked = outDebt.daysBehind > 60 ? '_blocked' : '';
    const noBlocked = outDebt.daysBehind < 60 ? '_no_blocked' : '';
    const hasWeightedRateSubKey = isBogota && !!outDebt.extra?.maxTerm ? '_weighted_rate' : '';
    if (isConfirmedByUser) return `${baseKey}${prePayment}${blocked}${noBlocked}${hasWeightedRateSubKey}`;
    if (isRejected) return `negotiation_${option.type}_confirmation_description_${state}`;
    return `negotiation_${option.type}_confirmation_description_${state}${blocked}${noBlocked}${hasWeightedRateSubKey}`;
  }

  if (isBogota && isBalanceBehindPayment) {
    const baseKey = `alert_paymentAlert_${option.type}_content`;
    const prePayment = alternative.prePayment ? '_prePayment' : '';
    const subType = `_${alternative.type}`;

    if (isConfirmedByUser) return `${baseKey}${subType}${prePayment}`;
    return `negotiation_${option.type}_confirmation_description_${state}${subType}`;
  }

  const {
    daysBehindLimitSubKey, isDaysBehindLimit,
  } = getDaysBehindLimitKeyRappi(outDebt, option);

  if (isMovistarMoney && isCatchUp) {
    if (isConfirmedByUser) return `alert_paymentAlert_content_${option.type}`;
  }

  const isAcpkJcap = isJcap && isACPK;
  if (isConfirmedByUser && !isPaymentPromise
    && !((isRappiPay || isRappiPrestamo) && (isRestructure || isRefinance || isQuotification))) {
    const adn = isAdn ? '_ADN' : '';
    const totalPayment = isTotalPayment ? `_totalPayment${adn}` : '';
    const prePayment = (alternative.prePayment && !isAcpkJcap) ? '_prePayment' : '';
    const firstPayment = (alternative.prePayment && isAcpkJcap) ? '_firstPayment' : '';
    const virtualDebtAcpkJCap = isVirtualDebt && isAcpkJcap ? '_virtual' : '';
    const contentValue = totalPayment || prePayment || virtualDebtAcpkJCap || firstPayment;
    const davNormalizationInflation = (isDav && isNormalization) ? '_interestInflation' : '';
    const contentExtra = getContentExtraBankNegType(outDebt, option) || davNormalizationInflation;
    const discountValue = (alternative.discountValue && isDav && isACPK) ? '_discountText' : '';
    return `alert_paymentAlert_content${contentValue}${contentExtra}${discountValue}${daysBehindLimitSubKey}`;
  }

  const virtual = isVirtualDebt ? 'VIRTUAL' : '';
  const prePayment = alternative.prePayment ? 'prePayment_' : '';
  const fallenSub = outDebt.extra?.isFallen ? '_fallen' : '';
  if (
    (isRappiPay || isRappiPrestamo)
    && isRestructure
    && isConfirmedByUser
  ) return `negotiation_${option.type}${virtual}_confirmation_description_${prePayment}${state}${fallenSub}`;

  if (isDaysBehindLimit) {
    return `negotiation_${option.type}${virtual}_confirmation_description_${prePayment}${state}${daysBehindLimitSubKey}`;
  }

  if (
    isScotiabank && isRefinance && isRejected
  ) return `negotiation_${option.type}_confirmation_description_${prePayment}${state}_${outDebt.extra?.normative026}`;

  return `negotiation_${option.type}${virtual}_confirmation_description_${prePayment}${state}`;
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  alert: {
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
}));

export default NegotiationStateAlert;
